import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import ReferralCard from '../components/ReferralCard';
import ShareScoreModal from '../components/ShareScoreModal';
import PrivacyChecklistEnhanced from '../components/PrivacyChecklistEnhanced';
import { 
  Shield, 
  MapPin, 
  Eye, 
  Key, 
  Smartphone, 
  Lock,
  MessageSquare,
  BookOpen,
  ArrowRight,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  Share2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [privacyData, setPrivacyData] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [checks, setChecks] = useState({
    has_location_services: true,
    has_ad_tracking: true,
    uses_vpn: false,
    has_strong_passwords: false,
    two_factor_enabled: false,
    uses_encrypted_messaging: false,
    reviews_app_permissions: false,
    uses_privacy_browser: false,
  });

  const calculateScore = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/privacy-score`, checks);
      setPrivacyData(response.data);
      updateUser({ privacy_score: response.data.score });
      toast.success('Privacy score updated!');
    } catch (error) {
      toast.error('Failed to calculate score');
    } finally {
      setLoading(false);
    }
  }, [checks, updateUser]);

  useEffect(() => {
    // Initial score calculation
    calculateScore();
  }, []);

  const toggleCheck = (key) => {
    setChecks(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const score = privacyData?.score || user?.privacy_score || 0;
  const grade = privacyData?.grade || (score >= 80 ? 'A' : score >= 60 ? 'B' : score >= 40 ? 'C' : score >= 20 ? 'D' : 'F');
  
  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A': return 'text-[#00FF94]';
      case 'B': return 'text-[#22C55E]';
      case 'C': return 'text-yellow-500';
      case 'D': return 'text-orange-500';
      default: return 'text-red-500';
    }
  };

  const checkItems = [
    { key: 'has_location_services', label: 'Location Services Disabled', icon: MapPin, inverted: true },
    { key: 'has_ad_tracking', label: 'Ad Tracking Disabled', icon: Eye, inverted: true },
    { key: 'uses_vpn', label: 'Using VPN', icon: Shield, inverted: false },
    { key: 'has_strong_passwords', label: 'Strong Passwords', icon: Key, inverted: false },
    { key: 'two_factor_enabled', label: 'Two-Factor Auth', icon: Lock, inverted: false },
    { key: 'uses_encrypted_messaging', label: 'Encrypted Messaging', icon: MessageSquare, inverted: false },
    { key: 'reviews_app_permissions', label: 'Review App Permissions', icon: Smartphone, inverted: false },
    { key: 'uses_privacy_browser', label: 'Privacy Browser', icon: Shield, inverted: false },
  ];

  const quickActions = [
    { label: 'AI Assistant', to: '/chat', icon: MessageSquare, description: 'Get privacy help' },
    { label: 'Protection Guides', to: '/toolkit', icon: BookOpen, description: 'Step-by-step guides' },
    { label: 'Privacy News', to: '/news', icon: TrendingUp, description: 'Latest updates' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl text-white mb-2">
            Welcome back, {user?.name}
          </h1>
          <p className="text-gray-500">
            Monitor and improve your digital privacy health
          </p>
        </div>

        {/* Main Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {/* Privacy Score Card - Large */}
          <Card className="md:col-span-2 bg-[#0a0a0a] border-[#262626] overflow-hidden relative" data-testid="privacy-score-card">
            <div className="absolute inset-0 bg-hero-gradient opacity-30" />
            <CardContent className="relative p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <CardTitle className="text-white text-xl mb-1">Privacy Score</CardTitle>
                  <CardDescription className="text-gray-500">Your current protection level</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => setShowShareModal(true)}
                    className="text-gray-400 hover:text-[#00FF94]"
                    data-testid="share-score-btn"
                  >
                    <Share2 className="w-5 h-5" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={calculateScore}
                    disabled={loading}
                    className="text-gray-400 hover:text-white"
                    data-testid="refresh-score-btn"
                  >
                    <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-8">
                {/* Circular Score */}
                <div className="relative w-36 h-36">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      stroke="#262626"
                      strokeWidth="8"
                      fill="none"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      stroke="#00FF94"
                      strokeWidth="8"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray="283"
                      strokeDashoffset={283 - (283 * score) / 100}
                      className="score-circle"
                      style={{ filter: 'drop-shadow(0 0 6px rgba(0, 255, 148, 0.5))' }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="font-mono font-bold text-4xl text-white" data-testid="privacy-score-value">{score}</span>
                    <span className={`font-mono text-sm ${getGradeColor(grade)}`}>Grade {grade}</span>
                  </div>
                </div>

                {/* Score Breakdown */}
                <div className="flex-1">
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-400">Tracking Protection</span>
                        <span className="text-white font-mono">{(!checks.has_location_services && !checks.has_ad_tracking) ? '30' : checks.has_location_services && checks.has_ad_tracking ? '0' : '15'}/30</span>
                      </div>
                      <Progress value={(!checks.has_location_services && !checks.has_ad_tracking) ? 100 : checks.has_location_services && checks.has_ad_tracking ? 0 : 50} className="h-2 bg-[#262626]" />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-400">Security Measures</span>
                        <span className="text-white font-mono">{(checks.uses_vpn ? 15 : 0) + (checks.has_strong_passwords ? 15 : 0) + (checks.two_factor_enabled ? 15 : 0)}/45</span>
                      </div>
                      <Progress value={((checks.uses_vpn ? 15 : 0) + (checks.has_strong_passwords ? 15 : 0) + (checks.two_factor_enabled ? 15 : 0)) / 45 * 100} className="h-2 bg-[#262626]" />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-400">Privacy Tools</span>
                        <span className="text-white font-mono">{(checks.uses_encrypted_messaging ? 10 : 0) + (checks.reviews_app_permissions ? 8 : 0) + (checks.uses_privacy_browser ? 7 : 0)}/25</span>
                      </div>
                      <Progress value={((checks.uses_encrypted_messaging ? 10 : 0) + (checks.reviews_app_permissions ? 8 : 0) + (checks.uses_privacy_browser ? 7 : 0)) / 25 * 100} className="h-2 bg-[#262626]" />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions Card */}
          <Card className="bg-[#0a0a0a] border-[#262626]" data-testid="quick-actions-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-white text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {quickActions.map((action, index) => (
                <Link 
                  key={index}
                  to={action.to}
                  className="flex items-center gap-3 p-3 rounded-lg bg-[#050505] border border-[#262626] hover:border-[#00FF94]/30 transition-colors group"
                  data-testid={`quick-action-${index}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#00FF94]/10 flex items-center justify-center group-hover:bg-[#00FF94]/20 transition-colors">
                    <action.icon className="w-5 h-5 text-[#00FF94]" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-white">{action.label}</div>
                    <div className="text-xs text-gray-500">{action.description}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-[#00FF94] transition-colors" />
                </Link>
              ))}
            </CardContent>
          </Card>

          {/* Status Card */}
          <Card className="bg-[#0a0a0a] border-[#262626]" data-testid="status-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-white text-lg">Account Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Plan</span>
                  <span className={`px-2 py-1 text-xs rounded-full font-mono ${user?.is_premium ? 'bg-[#00FF94]/10 text-[#00FF94] border border-[#00FF94]/20' : 'bg-[#262626] text-gray-400'}`}>
                    {user?.is_premium ? 'Premium' : 'Free'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">AI Queries</span>
                  <span className="text-white text-sm font-mono">{user?.is_premium ? 'Unlimited' : '5/day'}</span>
                </div>
                {!user?.is_premium && (
                  <Link to="/pricing">
                    <Button className="w-full btn-primary h-9 text-sm mt-2" data-testid="upgrade-btn">
                      Upgrade to Premium
                    </Button>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Privacy Checklist - Full Width */}
          <Card className="md:col-span-3 lg:col-span-4 bg-[#0a0a0a] border-[#262626]" data-testid="privacy-checklist-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white text-xl">Privacy Checklist</CardTitle>
                  <CardDescription className="text-gray-500">
                    Toggle what you've implemented and click each item to see recommended tools
                  </CardDescription>
                </div>
                <Button onClick={calculateScore} disabled={loading} className="btn-primary" data-testid="update-score-btn">
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                  Update Score
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <PrivacyChecklistEnhanced 
                checks={checks}
                onToggle={toggleCheck}
                onUpdateScore={calculateScore}
                loading={loading}
              />
            </CardContent>
          </Card>

          {/* Recommendations Card */}
          {privacyData?.recommendations && privacyData.recommendations.length > 0 && (
            <Card className="md:col-span-2 lg:col-span-2 bg-[#0a0a0a] border-[#262626]" data-testid="recommendations-card">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {privacyData.recommendations.slice(0, 5).map((rec, index) => (
                    <li key={index} className="flex items-start gap-3 text-sm">
                      <div className="w-5 h-5 rounded-full bg-yellow-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-yellow-500 text-xs font-mono">{index + 1}</span>
                      </div>
                      <span className="text-gray-400">{rec}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Protection Status */}
          <Card className="md:col-span-1 lg:col-span-2 bg-[#0a0a0a] border-[#262626]" data-testid="protection-status-card">
            <CardHeader>
              <CardTitle className="text-white text-lg">Protection Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { label: 'Location Tracking', protected: !checks.has_location_services },
                  { label: 'Advertising ID', protected: !checks.has_ad_tracking },
                  { label: 'Network Traffic', protected: checks.uses_vpn },
                  { label: 'Account Security', protected: checks.two_factor_enabled },
                ].map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">{item.label}</span>
                    <div className={`flex items-center gap-1 text-xs font-mono ${item.protected ? 'text-[#00FF94]' : 'text-red-400'}`}>
                      {item.protected ? (
                        <>
                          <CheckCircle2 className="w-3 h-3" />
                          Protected
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="w-3 h-3" />
                          At Risk
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Referral Card */}
          <div className="md:col-span-2">
            <ReferralCard />
          </div>
        </div>
      </div>

      {/* Share Score Modal */}
      {showShareModal && (
        <ShareScoreModal 
          score={score} 
          grade={grade} 
          onClose={() => setShowShareModal(false)} 
        />
      )}
    </div>
  );
}
